/* Widget "Le mie richieste" — visibile a role=dipendente/admin/super_admin loggati.
 *
 * Mostra:
 *  - N. richieste attualmente in carico (assigned_to=me, stato ∈ nuovo|in_lavorazione|preventivato)
 *  - N. in attesa di primo contatto (nuovo + assigned_to=me)
 *  - Shortcut "Apri Kanban filtrato" → /admin/richieste?assigned_to=me
 *
 * Failsafe: se l'API risponde 401/403 o error, non renderizza nulla (fail-silent).
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ClipboardList, ArrowRight, Clock, TrendingUp } from "lucide-react";
import { useAuth } from "@/App";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

export default function MyRichiesteWidget() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        // Filtra sull'utente corrente. Il backend supporta assigned_to=me.
        const r = await axios.get(`${API}/admin/richieste?assigned_to=me&limit=200`, {
          withCredentials: true,
        });
        if (!mounted) return;
        const items = r.data?.items || [];
        const inCharge = items.filter(
          (i) => ["nuovo", "in_lavorazione", "preventivato"].includes(i.stato)
        );
        const nuovi = inCharge.filter((i) => i.stato === "nuovo");
        // "in scadenza follow-up" = created > 3 giorni fa e ancora "nuovo" o "in_lavorazione"
        const now = Date.now();
        const staleThreshold = 3 * 24 * 60 * 60 * 1000;
        const staleFollowUp = inCharge.filter((i) => {
          const created = i.created_at ? new Date(i.created_at).getTime() : 0;
          return now - created > staleThreshold && i.stato !== "chiuso_positivo";
        });
        setStats({
          in_carico: inCharge.length,
          nuovi: nuovi.length,
          scadenza_follow_up: staleFollowUp.length,
        });
      } catch {
        // fail-silent
        setStats(null);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  // Solo staff (super_admin/admin/dipendente); intermediario e cliente non lo vedono
  const staffRoles = ["super_admin", "admin", "dipendente"];
  if (!user || !staffRoles.includes(user.role)) return null;
  if (loading) return null;
  if (!stats) return null;

  return (
    <div
      className="mb-6 rounded-2xl bg-gradient-to-br from-[#163321] to-[#0e1f15] text-white p-5 sm:p-6 shadow-lg"
      data-testid="widget-mie-richieste"
    >
      <div className="flex items-start justify-between mb-4 gap-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <ClipboardList className="w-5 h-5 text-amber-300" />
            <h3 className="font-heading text-lg font-bold">Le mie richieste da lavorare</h3>
          </div>
          <p className="text-xs text-emerald-100/80">
            Ciao {user?.name || user?.email?.split("@")[0]}, ecco cosa è in carico a te.
          </p>
        </div>
        <Link
          to="/admin/richieste?assigned_to=me"
          data-testid="widget-mie-open-kanban"
          className="inline-flex items-center gap-1.5 min-h-[40px] px-4 py-2 rounded-full bg-amber-400 text-[#163321] text-sm font-bold hover:bg-amber-300 whitespace-nowrap flex-shrink-0"
        >
          Apri Kanban <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <StatCard
          icon={ClipboardList}
          label="In carico"
          value={stats.in_carico}
          tid="widget-mie-in-carico"
        />
        <StatCard
          icon={TrendingUp}
          label="Nuove"
          value={stats.nuovi}
          tid="widget-mie-nuovi"
          highlight={stats.nuovi > 0}
        />
        <StatCard
          icon={Clock}
          label="Follow-up >3gg"
          value={stats.scadenza_follow_up}
          tid="widget-mie-scadenza"
          highlight={stats.scadenza_follow_up > 0}
          highlightColor="text-red-300"
        />
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, tid, highlight, highlightColor }) {
  const valueColor = highlight ? (highlightColor || "text-amber-300") : "text-white";
  return (
    <div
      className="p-3 rounded-xl bg-white/10 backdrop-blur-sm text-center"
      data-testid={tid}
    >
      <Icon className="w-4 h-4 mx-auto mb-1 opacity-80" />
      <div className={`text-2xl font-black ${valueColor}`}>{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-emerald-100/90">{label}</div>
    </div>
  );
}
